/**
 * BookCard — search-result card. Visually matches Bookshelf's BookCard
 * (spine aspect ratio, hover lift, cover as hero) but is intentionally a
 * separate component: this book may not be on the shelf yet, so it needs
 * "Add to TBR" instead of Bookshelf's full status-management menu, and
 * must work without a BookshelfContext-owned id.
 */

import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Bookmark, Check } from 'lucide-react';
import { cx } from '@/components/site/primitives';
import { toast } from '@/components/ui/sonner';
import { useBookshelf } from '@/components/bookshelf';
import { toShelfBook, getCanonicalBookId } from '@/lib/bookModel';
import BookCoverArt from './BookCoverArt';

export default function BookCard({ book, onOpenDetails }) {
  const { books, addBook } = useBookshelf();
  const reduceMotion = useReducedMotion();

  const shelfId = getCanonicalBookId(book);
  const isOnShelf = books.some((b) => b.id === shelfId || b.title.toLowerCase() === book.title.toLowerCase());

  const handleSave = (e) => {
    e.stopPropagation();
    if (isOnShelf) return;
    addBook(toShelfBook(book, 'shelf_tbr'), 'shelf_tbr');
    toast(`Added “${book.title}” to TBR`);
  };

  return (
    <motion.article layout="position" className="group relative" data-testid="ol-book-card">
      <motion.button
        type="button"
        onClick={() => onOpenDetails(book)}
        whileHover={reduceMotion ? undefined : { y: -8, rotate: -1 }}
        transition={{ type: 'spring', stiffness: 320, damping: 22 }}
        className="block w-full rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral focus-visible:ring-offset-2 focus-visible:ring-offset-cream"
        aria-label={`${book.title}${book.authors?.[0] ? ` by ${book.authors[0]}` : ''}`}
      >
        <BookCoverArt
          book={book}
          className="aspect-[2/3] shadow-card transition-shadow duration-300 group-hover:shadow-lift"
        />
      </motion.button>

      <button
        type="button"
        onClick={handleSave}
        aria-label={isOnShelf ? 'On your bookshelf' : `Add ${book.title} to TBR`}
        data-testid="ol-book-card-save"
        className={cx(
          'absolute right-1.5 top-1.5 grid h-7 w-7 place-items-center rounded-full backdrop-blur-sm transition-opacity',
          isOnShelf ? 'bg-coral text-white opacity-100' : 'bg-black/30 text-white opacity-0 group-hover:opacity-100 group-focus-within:opacity-100'
        )}
      >
        {isOnShelf ? <Check className="h-3.5 w-3.5" /> : <Bookmark className="h-3.5 w-3.5" />}
      </button>

      <div className="mt-2.5 px-0.5">
        <h3 className="line-clamp-2 font-display text-[13.5px] font-semibold leading-snug text-ink">{book.title}</h3>
        {book.authors?.[0] && <p className="mt-0.5 truncate text-xs text-subtle">{book.authors[0]}</p>}
        {book.publishYear && <p className="mt-1 text-[10.5px] text-subtle/80">{book.publishYear}</p>}
      </div>
    </motion.article>
  );
}
